<template>
  <div class="min-h-screen overflow-x-hidden bg-gray-50 px-4 py-6 sm:px-6 lg:px-8" data-testid="survey-studio">
    <div class="mx-auto max-w-7xl space-y-6">
      <header class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div class="max-w-3xl">
          <div class="mb-2 flex items-center gap-2 text-sm font-medium text-blue-700">
            <span class="inline-block h-2 w-2 rounded-full bg-blue-600"></span>
            Survey Studio
          </div>
          <h1 class="text-3xl font-bold tracking-tight text-gray-900">Build and preview your survey</h1>
          <p class="mt-2 text-base leading-6 text-gray-600">
            Draft changes are private. Use the participant preview to check your questions before publishing.
          </p>
        </div>
        <div class="w-full lg:w-80">
          <label for="survey-university" class="mb-1 block text-sm font-medium text-gray-700">University</label>
          <select
            id="survey-university"
            v-model="selectedUniversityId"
            :disabled="jsonEditing"
            class="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 shadow-sm"
            @change="loadUniversity"
          >
            <option disabled value="">Choose a university</option>
            <option v-for="university in universities" :key="university.id" :value="university.id">
              {{ university.name }}
            </option>
          </select>
        </div>
      </header>

      <div class="grid gap-3 sm:grid-cols-3" aria-label="Survey workflow">
        <div class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-blue-700">1 · Build</div>
          <div class="mt-1 text-sm text-blue-950">Edit a private draft without changing live forms.</div>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-gray-600">2 · Preview</div>
          <div class="mt-1 text-sm text-gray-700">Try the participant form and its conditions.</div>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-gray-600">3 · Publish</div>
          <div class="mt-1 text-sm text-gray-700">Publish a saved version, then apply it to ceremonies.</div>
        </div>
      </div>

      <div v-if="loading" class="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-600">
        Loading Survey Studio…
      </div>
      <div v-else-if="pageError" class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
        {{ pageError }}
      </div>
      <div v-else-if="!selectedUniversityId" class="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
        <h2 class="text-lg font-semibold text-gray-900">Choose a university to begin</h2>
        <p class="mt-2 text-sm text-gray-600">You will only see universities your account can manage.</p>
      </div>

      <div v-else class="grid min-w-0 gap-6 lg:grid-cols-12">
        <aside class="min-w-0 self-start rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:col-span-3 lg:sticky lg:top-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 class="font-semibold text-gray-900">Surveys</h2>
              <p class="text-xs text-gray-500">{{ surveys.length }} in this library</p>
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                :disabled="jsonEditing"
                @click="startCopying"
              >
                New from existing
              </button>
              <button
                type="button"
                class="rounded-md border border-blue-700 bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                :disabled="jsonEditing" @click="startCreating"
              >
                New
              </button>
            </div>
          </div>

          <div v-if="creating" class="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <label for="new-survey-name" class="block text-xs font-semibold text-blue-900">Survey name</label>
            <input
              id="new-survey-name"
              v-model.trim="newSurveyName"
              class="mt-1 h-10 w-full rounded-md border border-blue-200 bg-white px-3 text-sm"
              placeholder="e.g. Graduate outcomes 2026"
              @keyup.enter="createSurvey"
            />
            <div class="mt-2 flex gap-2">
              <button
                type="button"
                class="rounded-md bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                :disabled="!newSurveyName || saving"
                @click="createSurvey"
              >
                Create draft
              </button>
              <button type="button" class="px-2 py-1.5 text-xs text-gray-600" @click="creating = false">Cancel</button>
            </div>
          </div>

          <div v-if="copying" class="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="copy-survey-panel">
            <div class="text-sm font-semibold text-blue-950">New survey from existing</div>
            <p class="mt-1 text-xs leading-5 text-blue-800">This creates a separate survey family. Editing it cannot change the original survey.</p>
            <label for="copy-source-ceremony" class="mt-3 block text-xs font-semibold text-blue-900">Copy survey from</label>
            <select id="copy-source-ceremony" v-model="copySource" class="mt-1 h-10 w-full rounded-md border border-blue-200 bg-white px-2 text-sm">
              <option disabled value="">Choose a published version or ceremony</option>
              <optgroup label="Published survey versions">
                <option v-for="version in copyVersions" :key="version.id" :value="`version:${version.id}`">{{ version.label }}</option>
              </optgroup>
              <option v-for="ceremony in ceremoniesNewestFirst" :key="ceremony.id" :value="ceremony.id">{{ ceremonyLabel(ceremony) }}</option>
            </select>
            <label for="copy-survey-name" class="mt-3 block text-xs font-semibold text-blue-900">New draft name</label>
            <input id="copy-survey-name" v-model.trim="copySurveyName" class="mt-1 h-10 w-full rounded-md border border-blue-200 bg-white px-3 text-sm" placeholder="e.g. Careers survey 2027" />
            <fieldset class="mt-3">
              <legend class="text-xs font-semibold text-blue-900">Prepare for ceremonies</legend>
              <div class="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-blue-100 bg-white p-2">
                <label v-for="ceremony in ceremoniesNewestFirst" :key="`copy-target-${ceremony.id}`" class="flex items-start gap-2 rounded px-1 py-1 text-xs text-gray-700">
                  <input v-model="copyTargetCeremonyIds" type="checkbox" :value="ceremony.id" class="mt-0.5 h-4 w-4" />
                  <span>{{ ceremonyLabel(ceremony) }}</span>
                </label>
              </div>
            </fieldset>
            <div class="mt-3 flex gap-2">
              <button type="button" class="rounded-md bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" :disabled="!canCopySurvey || saving" @click="copySurvey">
                Create independent draft
              </button>
              <button type="button" class="px-2 py-1.5 text-xs text-gray-600" @click="copying = false">Cancel</button>
            </div>
          </div>

          <div v-if="surveys.length" class="space-y-2">
            <button
              v-for="item in surveys"
              :key="item.id"
              type="button"
              class="w-full rounded-lg border p-3 text-left transition"
              :class="item.id === selectedSurveyId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'"
              :disabled="lifecycleBusy || jsonEditing"
              @click="selectSurvey(item.id)"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="text-sm font-semibold text-gray-900">{{ item.name }}</span>
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-semibold"
                  :class="item.draftVersion ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'"
                >
                  {{ item.draftVersion ? 'Draft' : 'Published' }}
                </span>
              </div>
              <div class="mt-2 text-xs text-gray-500">
                {{ libraryVersion(item).definition.fields.length }} questions
                <span> · Used by {{ assignedCount(item) }} {{ assignedCount(item) === 1 ? 'ceremony' : 'ceremonies' }}</span>
              </div>
            </button>
          </div>
          <div v-else-if="!creating" class="rounded-lg border border-dashed border-gray-300 p-5 text-center">
            <div class="text-sm font-medium text-gray-800">No surveys yet</div>
            <p class="mt-1 text-xs text-gray-500">Create a draft; nothing will go live.</p>
          </div>
        </aside>

        <main v-if="survey" class="min-w-0 space-y-4 lg:col-span-9">
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div class="min-w-0 flex-1">
                <div class="mb-3 flex flex-wrap items-center gap-2">
                  <label for="survey-version" class="text-sm font-medium">Version</label>
                  <select id="survey-version" v-model="selectedVersionId" :disabled="dirty || lifecycleBusy || jsonEditing" class="rounded-lg border border-gray-300 bg-white p-2 text-sm" @change="hydrateSurvey(survey, selectedVersionId)">
                    <option v-for="version in survey.versions" :key="version.id" :value="version.id">v{{ version.version }} · {{ version.status === 'DRAFT' ? 'Draft' : 'Published' }} · {{ version.assignedCount || 0 }} ceremonies</option>
                  </select>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="isPublished ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'">{{ isPublished ? 'Published' : 'Draft' }} v{{ workingVersion.version }}</span>
                  <span class="rounded-full px-2.5 py-1 text-xs font-medium" :class="isPublished ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'">{{ workingVersion.assignedCount ? `Used by ${workingVersion.assignedCount} ceremonies` : 'Not assigned' }}</span>
                  <span v-if="dirty" class="text-xs font-medium text-amber-700">Unsaved changes</span>
                  <span v-else-if="savedAt" class="text-xs text-gray-500">Saved {{ savedAt }}</span>
                </div>
                <label for="survey-name" class="sr-only">Internal survey name</label>
                <input
                  id="survey-name"
                  v-model="surveyName"
                  :disabled="isPublished || jsonEditing"
                  class="mt-2 w-full border-0 border-b border-transparent bg-transparent p-0 text-2xl font-bold text-gray-900 focus:border-blue-500 focus:outline-none"
                  aria-describedby="survey-name-help"
                />
                <p id="survey-name-help" class="mt-1 text-xs text-gray-500">Internal name shown only in Survey Studio.</p>
              </div>
              <div v-if="!isPublished" class="flex flex-wrap gap-2">
                <button type="button" class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 disabled:opacity-50" :disabled="jsonEditing || saving || publishing" @click="openJsonEditor">Edit JSON</button>
                <button
                  type="button"
                  class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                  :disabled="jsonEditing" @click="checkSurvey"
                >
                  Check survey
                </button>
                <button
                  type="button"
                  class="rounded-lg border border-blue-700 bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="saving || !dirty || jsonEditing"
                  @click="saveSurvey"
                >
                  {{ saving ? 'Saving…' : 'Save draft' }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="lifecycleError" role="alert" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{{ lifecycleError }}</div>
          <div v-if="lifecycleMessage" role="status" class="rounded-lg bg-green-50 p-4 text-sm text-green-800">{{ lifecycleMessage }}</div>

          <div v-if="!isPublished" class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5" data-testid="publish-gate">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Ready to publish?</h2>
                <p class="mt-1 text-sm text-gray-500">Publish a valid saved draft. Ceremony assignments stay unchanged until you apply the published version.</p>
                <ul class="mt-3 space-y-2 text-sm">
                  <li v-for="check in publishGate.checks" :key="check.key" class="flex items-center gap-2" :class="check.passed ? 'text-green-800' : 'text-gray-600'">
                    <span class="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold" :class="check.passed ? 'bg-green-100' : 'bg-gray-100'">{{ check.passed ? '✓' : '·' }}</span>
                    <span>{{ check.label }}</span>
                  </li>
                </ul>
              </div>
              <button type="button" class="rounded-lg border border-green-700 bg-green-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-40" :disabled="dirty || !publishGate.ready || publishing || jsonEditing" @click="publishSurvey">
                {{ publishing ? 'Publishing…' : 'Publish version' }}
              </button>
            </div>
          </div>

          <div
            v-if="!isPublished && validationMessage"
            class="rounded-xl border p-4 text-sm"
            :class="validationErrors.length ? 'border-red-200 bg-red-50 text-red-900' : 'border-green-200 bg-green-50 text-green-900'"
            role="status"
          >
            <div class="font-semibold">{{ validationMessage }}</div>
            <ul v-if="validationErrors.length" class="mt-2 list-disc space-y-1 pl-5">
              <li v-for="issue in validationErrors" :key="issue.path + issue.code">{{ issue.message }}</li>
            </ul>
          </div>

          <section v-if="jsonEditing" class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5" data-testid="survey-json-editor">
            <h2 class="text-lg font-semibold text-gray-900">Survey JSON</h2>
            <p id="survey-json-help" class="mt-1 text-sm text-gray-600">Paste a definition or import a JSON file. Select Use JSON to check it and update the draft preview. Then save the draft to keep the changes.</p>
            <label for="survey-json-file" class="mt-4 block text-sm font-medium">Import JSON file</label>
            <input id="survey-json-file" type="file" accept=".json,application/json" :disabled="jsonBusy" class="mt-1 block w-full text-sm" @change="importJsonFile" />
            <label for="survey-json" class="mt-4 block text-sm font-medium">Definition JSON</label>
            <textarea id="survey-json" v-model="jsonText" :disabled="jsonBusy" aria-describedby="survey-json-help" spellcheck="false" rows="20" class="mt-1 w-full rounded-lg border border-gray-300 p-3 font-mono text-sm"></textarea>
            <div v-if="jsonErrors.length" role="alert" class="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-900">
              <p class="font-semibold">The draft has not changed. Fix these items:</p>
              <ul class="mt-1 list-disc pl-5"><li v-for="(issue, index) in jsonErrors" :key="index">{{ issue.path ? issue.path + ': ' : '' }}{{ issue.message }}</li></ul>
            </div>
            <div class="mt-4 flex gap-3">
              <button type="button" :disabled="jsonBusy" class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" @click="applyJson">{{ jsonBusy ? 'Checking…' : 'Use JSON' }}</button>
              <button type="button" :disabled="jsonBusy" class="rounded-lg border border-gray-300 px-4 py-2 text-sm" @click="cancelJsonEditor">Cancel JSON</button>
            </div>
          </section>

          <div v-if="!isPublished && !jsonEditing" class="grid min-w-0 items-start gap-5 xl:grid-cols-12">
            <section class="min-w-0 space-y-4 xl:col-span-7">
              <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5" data-testid="draft-ceremonies">
                <h2 class="text-lg font-semibold text-gray-900">Ceremonies to prepare</h2>
                <p class="mt-1 text-sm text-gray-500">Optionally note where you plan to use this draft. Publishing does not change any ceremony. Review and apply it after publishing.</p>
                <div v-if="ceremonies.length" class="mt-4 grid gap-2 sm:grid-cols-2">
                  <label v-for="ceremony in ceremoniesNewestFirst" :key="`target-${ceremony.id}`" class="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm text-gray-700 hover:bg-gray-50">
                    <input v-model="targetCeremonyIds" type="checkbox" :value="ceremony.id" class="mt-0.5 h-4 w-4" />
                    <span>{{ ceremonyLabel(ceremony) }}</span>
                  </label>
                </div>
                <div v-else class="mt-4 rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">You can publish without choosing a ceremony.</div>
              </div>

              <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                <h2 class="text-lg font-semibold text-gray-900">Participant introduction</h2>
                <p class="mt-1 text-sm text-gray-500">This is what participants see above your questions.</p>
                <div class="mt-4 grid gap-4">
                  <div>
                    <label for="public-survey-title" class="mb-1 block text-sm font-medium text-gray-700">Survey heading</label>
                    <input id="public-survey-title" v-model="definition.title" class="h-11 w-full rounded-lg border border-gray-300 px-3" />
                  </div>
                  <div>
                    <label for="survey-description" class="mb-1 block text-sm font-medium text-gray-700">Short introduction <span class="font-normal text-gray-400">(optional)</span></label>
                    <textarea id="survey-description" v-model="definition.description" rows="3" class="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Explain why you are asking and how answers will be used."></textarea>
                  </div>
                </div>
              </div>

              <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 class="text-lg font-semibold text-gray-900">Questions</h2>
                    <p class="text-sm text-gray-500">Choose a question to edit it. The preview updates immediately.</p>
                  </div>
                  <select
                    v-model="newQuestionType"
                    class="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm"
                    aria-label="Question type to add"
                    @change="addQuestion"
                  >
                    <option value="">+ Add question</option>
                    <option v-for="type in questionTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
                  </select>
                </div>


                <div v-if="!definition.fields.length" class="mt-4 rounded-lg border-2 border-dashed border-gray-200 px-5 py-10 text-center">
                  <div class="text-sm font-semibold text-gray-900">Start with the first question</div>
                  <p class="mt-1 text-sm text-gray-500">Dropdowns and multiple choice are best when you want consistent reporting.</p>
                </div>
                <div v-else class="mt-4 space-y-2">
                  <div
                    v-for="(field, index) in definition.fields"
                    :key="field._editorId"
                    class="flex items-center gap-2 rounded-lg border p-2"
                    :class="selectedFieldIndex === index ? 'border-blue-500 bg-blue-50' : 'border-gray-200'"
                  >
                    <button type="button" class="min-w-0 flex-1 px-2 py-1 text-left" :data-testid="`question-card-${index}`" @click="selectField(index)">
                      <div class="truncate text-sm font-semibold text-gray-900">{{ field.title || 'Untitled question' }}</div>
                      <div class="mt-0.5 text-xs text-gray-500">{{ typeLabel(field.type) }}<span v-if="field.rules && field.rules.required"> · Required</span></div>
                    </button>
                    <button type="button" class="rounded p-2 text-gray-400 hover:bg-white hover:text-gray-700 disabled:opacity-30" :disabled="index === 0" :aria-label="`Move ${field.title} up`" @click="moveQuestion(index, -1)">↑</button>
                    <button type="button" class="rounded p-2 text-gray-400 hover:bg-white hover:text-gray-700 disabled:opacity-30" :disabled="index === definition.fields.length - 1" :aria-label="`Move ${field.title} down`" @click="moveQuestion(index, 1)">↓</button>
                  </div>
                </div>
              </div>

              <div v-if="selectedField" class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5" data-testid="question-editor">
                <div class="flex items-center justify-between gap-4">
                  <div>
                    <div class="text-xs font-semibold uppercase tracking-wide text-blue-700">Question {{ selectedFieldIndex + 1 }}</div>
                    <h2 class="mt-1 text-lg font-semibold text-gray-900">Edit question</h2>
                  </div>
                  <button type="button" class="text-sm font-medium text-red-700 hover:text-red-900" @click="removeQuestion">Remove</button>
                </div>
                <div class="mt-4 grid gap-4">
                  <div>
                    <label for="question-title" class="mb-1 block text-sm font-medium text-gray-700">{{ selectedField.type === 'markdown' ? 'Heading (optional)' : 'Question text' }}</label>
                    <textarea id="question-title" :value="selectedField.title" rows="2" class="w-full rounded-lg border border-gray-300 px-3 py-2" @input="selectedField.title = $event.target.value" @change="updateQuestionTitle($event.target.value)"></textarea>
                  </div>
                  <div>
                    <label for="title-format" class="block text-sm font-medium">Title format</label>
                    <select id="title-format" :value="selectedField.titleFormat || 'text'" @change="$set(selectedField, 'titleFormat', $event.target.value)">
                      <option value="text">Plain text</option><option value="markdown">Markdown</option>
                    </select>
                  </div>
                  <div v-if="selectedField.type === 'markdown'">
                    <label for="markdown-body" class="block text-sm font-medium">Markdown body</label>
                    <textarea id="markdown-body" :value="selectedField.content" rows="6" class="w-full rounded-lg border border-gray-300 px-3 py-2" @input="$set(selectedField, 'content', $event.target.value)"></textarea>
                  </div>
                  <p v-if="selectedField.type === 'markdown' || selectedField.titleFormat === 'markdown'" class="text-xs text-gray-500">Use **bold text**, [link text](https://example.com), and lines starting with - for lists.</p>
                  <div v-if="!isInformationField(selectedField)">
                    <label for="question-key" class="mb-1 block text-sm font-medium text-gray-700">Response column name</label>
                    <input id="question-key" :value="selectedField.name" aria-describedby="question-key-help" class="h-11 w-full rounded-lg border border-gray-300 px-3" @input="updateQuestionKey($event.target.value)" />
                    <p id="question-key-help" class="mt-1 text-xs text-gray-500">Identifies this question in saved responses. Keep this name stable when editing question wording.</p>
                    <p v-if="selectedQuestionKeyError" class="mt-1 text-sm text-red-700">{{ selectedQuestionKeyError }}</p>
                  </div>
                  <div class="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label for="question-type" class="mb-1 block text-sm font-medium text-gray-700">Answer type</label>
                      <select id="question-type" :value="selectedField.type" class="h-11 w-full rounded-lg border border-gray-300 bg-white px-3" @change="changeQuestionType($event.target.value)">
                        <option v-for="type in questionTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
                      </select>
                    </div>
                    <div v-if="!isInformationField(selectedField)" class="flex items-end pb-2">
                      <label class="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input v-model="selectedField.rules.required" type="checkbox" class="h-4 w-4" />
                        Participants must answer
                      </label>
                    </div>
                  </div>
                  <div v-if="!isInformationField(selectedField) && !isChoiceField(selectedField) && selectedField.type !== 'date'">
                    <label for="question-placeholder" class="mb-1 block text-sm font-medium text-gray-700">Example or hint <span class="font-normal text-gray-400">(optional)</span></label>
                    <input id="question-placeholder" v-model="selectedField.placeholder" class="h-11 w-full rounded-lg border border-gray-300 px-3" placeholder="Shown inside the empty answer box" />
                  </div>

                  <div v-if="isChoiceField(selectedField)" class="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div class="flex items-center justify-between">
                      <div>
                        <h3 class="text-sm font-semibold text-gray-900">Answer options</h3>
                        <p class="text-xs text-gray-500">Drag-free controls keep ordering predictable.</p>
                      </div>
                      <button type="button" class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700" @click="addChoice">Add option</button>
                    </div>
                    <div class="mt-3 space-y-2">
                      <div v-for="(choice, choiceIndex) in selectedField.choices" :key="choiceIndex" class="flex items-center gap-2">
                        <span class="w-5 text-right text-xs text-gray-400">{{ choiceIndex + 1 }}</span>
                        <input v-model="choice.text" class="h-10 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm" :aria-label="`Option ${choiceIndex + 1}`" />
                        <button type="button" class="rounded p-2 text-gray-400 hover:text-red-700" :aria-label="`Remove option ${choiceIndex + 1}`" @click="removeChoice(choiceIndex)">×</button>
                      </div>
                    </div>
                  </div>

                  <div v-if="['text', 'email'].includes(selectedField.type)" class="rounded-lg border border-gray-200 p-4">
                    <h3 class="text-sm font-semibold">Answer validation</h3>
                    <label v-for="rule in ['min', 'max']" :key="rule" class="block mt-2 text-sm">
                      {{ rule === 'min' ? 'Minimum characters' : 'Maximum characters' }}
                      <input :value="selectedField.rules[rule]" type="number" min="0" step="1" class="block border rounded px-2 py-1" @input="setLengthRule(rule, $event.target.value)" />
                    </label>
                    <label class="block mt-2 text-sm">Pattern (advanced regex)
                      <input :value="selectedField.rules.regex" class="block w-full border rounded px-2 py-1" @input="$set(selectedField.rules, 'regex', $event.target.value)" />
                    </label>
                    <p v-if="selectedField.regex" class="text-xs text-gray-500">Legacy pattern also applies: {{ selectedField.regex }}</p>
                    <p class="mt-2 text-xs text-gray-500">Uses the existing form validation rules. Add ^ and $ to match the whole answer.</p>
                  </div>
                  <div class="rounded-lg border border-gray-200 p-4">
                    <label class="flex items-start gap-3">
                      <input type="checkbox" class="mt-1 h-4 w-4" :checked="Boolean(displayConditions.length)" :disabled="!conditionSources.length" @change="toggleCondition($event.target.checked)" />
                      <span>
                        <span class="block text-sm font-semibold text-gray-900">Only show this block after a particular answer</span>
                        <span class="block text-xs text-gray-500">Useful for follow-up questions. Only earlier multiple-choice questions can control this one.</span>
                        <span v-if="displayConditions.length > 1" class="block text-xs text-gray-500">This block appears when any condition matches.</span>
                      </span>
                    </label>
                    <div v-for="(condition, index) in displayConditions" :key="index" class="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <label :for="`condition-source-${index}`" class="mb-1 block text-xs font-medium text-gray-600">After this question</label>
                        <select :id="`condition-source-${index}`" :value="condition.field" class="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm" @change="updateConditionSource(condition, $event.target.value)">
                          <option v-for="field in conditionSources" :key="field.name" :value="field.name">{{ field.title }}</option>
                        </select>
                      </div>
                      <div>
                        <label :for="`condition-answer-${index}`" class="mb-1 block text-xs font-medium text-gray-600">When the answer is</label>
                        <select :id="`condition-answer-${index}`" :value="condition.value" class="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm" @change="condition.value = $event.target.value">
                          <option v-for="choice in conditionChoices(condition)" :key="String(choice.value)" :value="choice.value">{{ choice.text }}</option>
                        </select>
                      </div>
                      <button v-if="displayConditions.length > 1" type="button" class="justify-self-start text-xs font-medium text-red-700" :aria-label="`Remove condition ${index + 1}`" @click="removeCondition(condition)">Remove</button>
                    </div>
                    <button v-if="displayConditions.length" type="button" class="mt-3 text-sm font-medium text-blue-700" aria-label="Add condition" @click="addCondition">+ Add condition</button>
                    <p v-else-if="!conditionSources.length" class="mt-2 text-xs text-amber-700">Add a dropdown, radio, or multiple-choice question above this one to create a follow-up.</p>
                  </div>
                </div>
              </div>
            </section>

            <aside class="min-w-0 space-y-4 xl:col-span-5 xl:sticky xl:top-4">
              <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <label for="preview-ceremony" class="mb-1 block text-sm font-medium text-gray-700">Preview for ceremony</label>
                <select id="preview-ceremony" v-model="previewCeremonyId" class="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm">
                  <option disabled value="">Choose a published version or ceremony</option>
              <optgroup label="Published survey versions">
                <option v-for="version in copyVersions" :key="version.id" :value="`version:${version.id}`">{{ version.label }}</option>
              </optgroup>
                  <option v-for="ceremony in ceremonies" :key="ceremony.id" :value="ceremony.id">{{ ceremonyLabel(ceremony) }}</option>
                </select>
                <p class="mt-2 text-xs text-gray-500">This selection only drives the preview. Ceremony assignment is a separate publish step.</p>
              </div>
              <SurveyParticipantPreview
                :fields="definition.fields"
                :ceremony-id="previewCeremonyId"
                :ceremony-label="selectedCeremonyLabel"
                :organiser-slug="selectedUniversity ? selectedUniversity.slug : 'preview'"
                :events="ceremonies"
                :preview-key="previewKey"
                :title="definition.title"
                :description="definition.description"
                :preview-message="previewMessage"
                @preview-submit="handlePreviewSubmit"
              />
            </aside>
          </div>

          <div v-else-if="isPublished" class="space-y-4" data-testid="survey-health">
            <div class="rounded-xl border border-green-200 bg-green-50 p-5">
              <h2 class="text-lg font-semibold text-green-950">Published version</h2>
              <p class="mt-1 text-sm text-green-800">The questions are locked. Currently used by {{ workingVersion.assignedCount || 0 }} ceremonies.</p>
              <ul v-if="workingVersion.assignedCeremonyIds.length" class="mt-2 list-disc pl-5 text-sm text-green-900">
                <li v-for="id in workingVersion.assignedCeremonyIds" :key="id">{{ ceremonyName(id) }}</li>
              </ul>
              <button type="button" class="mt-4 rounded-lg border border-green-700 bg-white px-4 py-2 text-sm font-semibold text-green-900 disabled:opacity-50" :disabled="Boolean(survey.draftVersion) || lifecycleBusy" @click="amendSurvey">Amend this version</button>
              <p v-if="survey.draftVersion" class="mt-2 text-sm text-green-800">A draft already exists. Select it above to continue editing.</p>
            </div>
            <section class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5" aria-labelledby="apply-heading">
              <h2 id="apply-heading" class="text-lg font-semibold text-gray-900">Apply to ceremonies</h2>
              <p class="mt-1 text-sm text-gray-600">Only selected ceremonies will change. Their existing survey-check setting stays unchanged.</p>
              <div class="mt-4 grid gap-2 sm:grid-cols-2">
                <label v-for="ceremony in ceremoniesNewestFirst" :key="ceremony.id" class="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                  <input v-model="applyCeremonyIds" :disabled="lifecycleBusy || jsonEditing" type="checkbox" :value="ceremony.id" class="mt-0.5 h-4 w-4" />
                  <span>{{ ceremonyLabel(ceremony) }}</span>
                </label>
              </div>
              <button type="button" class="mt-4 rounded-lg border border-blue-700 px-4 py-2 text-sm font-semibold text-blue-800 disabled:opacity-50" :disabled="!applyCeremonyIds.length || lifecycleBusy" @click="reviewApplication">Review changes</button>
              <div v-if="applicationReview" class="mt-4 space-y-4" data-testid="application-summary">
                <article v-for="item in applicationReview.ceremonies" :key="item.ceremonyId" class="rounded-lg bg-gray-50 p-4 text-sm">
                  <h3 class="font-semibold">{{ ceremonyName(item.ceremonyId) }}</h3>
                  <p class="mt-1 text-gray-600">{{ item.source === 'none' ? 'This ceremony has no assigned survey. Registration fields stay unchanged.' : 'Compared with the currently assigned survey version. Registration fields stay unchanged.' }}</p>
                  <p v-if="!item.changes.formChanged" class="mt-2">No question or form changes.</p>
                  <template v-else>
                    <p v-if="item.changes.added.length" class="mt-2">Added: {{ item.changes.added.join('; ') }}</p>
                    <p v-if="item.changes.removed.length" class="mt-2">Removed: {{ item.changes.removed.join('; ') }}</p>
                    <p v-if="item.changes.changed.length" class="mt-2">Changed: {{ item.changes.changed.join('; ') }}</p>
                    <p v-for="(requirement, index) in item.changes.requirements" :key="index" class="mt-2 font-medium">{{ requirement.title }}: {{ requirement.required ? 'now required when shown' : 'now optional' }}</p>
                    <p v-if="!item.changes.added.length && !item.changes.removed.length && !item.changes.changed.length" class="mt-2">Form content, order or settings changed.</p>
                  </template>
                </article>
                <button type="button" class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" :disabled="lifecycleBusy || jsonEditing" @click="applyVersion">{{ lifecycleBusy ? 'Applying…' : 'Apply version to selected ceremonies' }}</button>
              </div>
            </section>
            <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-gray-900">Answer health</h2>
                  <p class="mt-1 text-sm text-gray-500">Counts below cover registrations recorded against this version in its currently assigned ceremonies. They are not current completion totals across amendments.</p>
                </div>
                <button type="button" class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700" :disabled="healthLoading" @click="loadHealth">{{ healthLoading ? 'Refreshing…' : 'Refresh' }}</button>
              </div>
              <div v-if="health" class="mt-5 grid gap-3 sm:grid-cols-3">
                <div class="rounded-lg bg-gray-50 p-4"><div class="text-2xl font-bold text-gray-900">{{ health.totals.registrations }}</div><div class="text-xs font-medium uppercase tracking-wide text-gray-500">Registrations</div></div>
                <div class="rounded-lg bg-gray-50 p-4"><div class="text-2xl font-bold text-gray-900">{{ health.totals.respondents }}</div><div class="text-xs font-medium uppercase tracking-wide text-gray-500">With survey answers</div></div>
                <div class="rounded-lg bg-gray-50 p-4"><div class="text-2xl font-bold text-gray-900">{{ health.totals.answers }}</div><div class="text-xs font-medium uppercase tracking-wide text-gray-500">Answers received</div></div>
              </div>
              <div v-if="health && health.ceremonies.length" class="mt-5 overflow-hidden rounded-lg border border-gray-200">
                <div v-for="item in health.ceremonies" :key="item.ceremonyId" class="grid gap-2 border-b border-gray-200 p-4 text-sm last:border-b-0 sm:grid-cols-4 sm:items-center">
                  <div class="font-semibold text-gray-900">{{ item.ceremonyLabel || ceremonyName(item.ceremonyId) }}</div>
                  <div class="text-gray-600">{{ item.registrations }} registrations</div>
                  <div class="text-gray-600">{{ item.respondents }} respondents</div>
                  <div class="text-gray-500">{{ item.lastAnswerAt ? `Last answer ${formatTimestamp(item.lastAnswerAt)}` : 'No answers yet' }}</div>
                </div>
              </div>
              <div v-else-if="!healthLoading" class="mt-5 rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-600">No answer-health data is available yet.</div>
            </div>
            <section aria-label="Published survey preview" class="space-y-4">
              <p class="text-sm text-gray-600">Preview the published participant form. Submission is disabled and answers entered here are not saved.</p>
              <div v-if="ceremonies.length > 1" class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <label for="published-preview-ceremony" class="mb-1 block text-sm font-medium text-gray-700">Preview for ceremony</label>
                <select id="published-preview-ceremony" v-model="previewCeremonyId" class="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm">
                  <option v-for="ceremony in ceremoniesNewestFirst" :key="ceremony.id" :value="ceremony.id">{{ ceremonyLabel(ceremony) }}</option>
                </select>
              </div>
              <SurveyParticipantPreview
                :fields="workingVersion.definition.fields"
                :ceremony-id="previewCeremonyId"
                :ceremony-label="selectedCeremonyLabel"
                :organiser-slug="selectedUniversity ? selectedUniversity.slug : 'preview'"
                :events="ceremonies"
                :preview-key="previewKey"
                :title="workingVersion.definition.title"
                :description="workingVersion.definition.description"
                :disable-submission="true"
              />
            </section>
          </div>
        </main>

        <div v-else-if="!creating" class="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center lg:col-span-9">
          <h2 class="text-lg font-semibold text-gray-900">Create the first survey draft</h2>
          <p class="mt-2 text-sm text-gray-600">A draft cannot affect registrations. Apply a published version to change a ceremony.</p>
          <button type="button" class="mt-4 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white" :disabled="jsonEditing" @click="startCreating">Create survey</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import SurveyParticipantPreview from './SurveyParticipantPreview.vue'
import { trackAdminEvent } from '../../admin/activity.js'
import { suggestQuestionKey, questionKeyError } from '../../surveys/questionKeys.js'

const CHOICE_TYPES = ['select', 'radio', 'multi-select']
const emptyPublishGate = () => ({
  ready: false,
  checks: [
    { key: 'definition', passed: false, label: 'Survey questions pass validation' },
  ],
})

export default {
  name: 'SurveyStudio',
  components: { SurveyParticipantPreview },
  data() {
    return {
      universities: [],
      selectedUniversityId: '',
      universityDetail: null,
      surveys: [],
      survey: null,
      surveyName: '',
      selectedVersionId: '',
      applyCeremonyIds: [],
      applicationReview: null,
      lifecycleBusy: false,
      lifecycleError: '',
      lifecycleMessage: '',
      definition: { schemaVersion: 1, title: '', description: '', fields: [] },
      selectedFieldIndex: -1,
      jsonEditing: false,
      jsonText: '',
      jsonErrors: [],
      jsonBusy: false,
      previewCeremonyId: '',
      loading: false,
      saving: false,
      pageError: '',
      validationErrors: [],
      validationMessage: '',
      creating: false,
      newSurveyName: '',
      copying: false,
      copySource: '',
      copySurveyName: '',
      copyTargetCeremonyIds: [],
      targetCeremonyIds: [],
      newQuestionType: '',
      dirty: false,
      hydrating: false,
      savedAt: '',
      publishGate: emptyPublishGate(),
      publishing: false,
      previewMessage: '',
      health: null,
      healthLoading: false,
      questionTypes: [
        { value: 'text', label: 'Short text' },
        { value: 'email', label: 'Email address' },
        { value: 'date', label: 'Date' },
        { value: 'select', label: 'Dropdown' },
        { value: 'radio', label: 'Single choice' },
        { value: 'multi-select', label: 'Multiple choice' },
        { value: 'presentationOnly', label: 'Information text' },
        { value: 'markdown', label: 'Markdown content' },
      ],
      editorSequence: 0,
    }
  },
  computed: {
    selectedQuestionKeyError() {
      return this.selectedField ? this.fieldKeyError(this.selectedField) : ''
    },
    selectedUniversity() {
      return this.universities.find(university => university.id === this.selectedUniversityId) || null
    },
    selectedSurveyId() {
      return this.survey ? this.survey.id : ''
    },
    draftVersion() {
      return (this.survey && this.survey.draftVersion) || { version: 1, ceremonyIds: [] }
    },
    workingVersion() {
      return (this.survey && this.survey.versions.find(version => version.id === this.selectedVersionId)) || { version: 1, ceremonyIds: [], assignedCeremonyIds: [] }
    },
    isPublished() {
      return this.workingVersion.status === 'PUBLISHED'
    },
    applicationKey() {
      return JSON.stringify([this.selectedSurveyId, this.selectedVersionId, [...this.applyCeremonyIds].sort()])
    },
    copyVersions() {
      return this.surveys.flatMap(survey => (survey.versions || []).filter(v => v.status === 'PUBLISHED').map(v => ({ id: v.id, label: `${survey.name} · v${v.version}` })))
    },
    ceremonies() {
      return (this.universityDetail && this.universityDetail.ceremonies) || []
    },
    ceremoniesNewestFirst() {
      return [...this.ceremonies].sort((left, right) => new Date(right.datetime) - new Date(left.datetime))
    },
    canCopySurvey() {
      return Boolean(this.copySource && this.copySurveyName)
    },
    selectedField() {
      return this.definition.fields[this.selectedFieldIndex] || null
    },
    conditionSources() {
      return this.definition.fields.slice(0, Math.max(0, this.selectedFieldIndex)).filter(field => this.isChoiceField(field))
    },
    displayConditions() {
      if (!this.selectedField) return []
      return (this.selectedField.conditions || []).filter(condition => (
        condition.comparator === 'equals' &&
        condition.operation &&
        condition.operation.relates_to === 'display' &&
        condition.operation.type === 'enabled' &&
        condition.operation.enabled === true
      ))
    },
    selectedCeremonyLabel() {
      const ceremony = this.ceremonies.find(item => item.id === this.previewCeremonyId)
      return ceremony ? this.ceremonyLabel(ceremony) : 'Choose a ceremony to test conditions'
    },
    previewKey() {
      return `${this.previewCeremonyId}:${JSON.stringify(this.definition.fields)}`
    },
  },
  watch: {
    previewKey() { this.previewMessage = '' },
    applicationKey() { this.applicationReview = null },
    definition: {
      deep: true,
      handler() {
        if (!this.hydrating && this.survey) this.dirty = true
      },
    },
    surveyName() {
      if (!this.hydrating && this.survey) this.dirty = true
    },
    targetCeremonyIds() {
      if (!this.hydrating && this.survey) {
        this.dirty = true
        this.previewMessage = ''
      }
    },
  },
  async created() {
    await this.loadUniversities()
  },
  methods: {
    setLengthRule(rule, value) {
      if (value === '') this.$delete(this.selectedField.rules, rule)
      else this.$set(this.selectedField.rules, rule, Number(value))
    },
    newEditorId() { return ++this.editorSequence },
    isInformationField(field) { return ['presentationOnly', 'markdown'].includes(field.type) },
    fieldKeyError(field, fields = this.definition.fields) {
      if (this.isInformationField(field)) return ''
      if (fields.filter(item => item.name === field.name).length > 1) return 'Each question needs a different key.'
      // Existing keys can remain unchanged, including older naming formats.
      return field.name === field._originalKey ? '' : questionKeyError(field.name)
    },
    questionKeyErrors(fields = this.definition.fields) {
      return fields.map((field, index) => ({
        path: `fields[${index}].name`, message: this.fieldKeyError(field, fields),
      })).filter(error => error.message)
    },
    async loadUniversities() {
      this.loading = true
      this.pageError = ''
      try {
        const response = await axios.get('/universities')
        this.universities = Array.isArray(response.data) ? response.data : []
        if (this.universities.length === 1) {
          this.selectedUniversityId = this.universities[0].id
          await this.loadUniversity()
        }
      } catch (error) {
        this.pageError = 'Could not load your universities. Please refresh and try again.'
      } finally {
        this.loading = false
      }
    },
    async loadUniversity() {
      if (!this.selectedUniversityId) return
      this.loading = true
      this.pageError = ''
      this.survey = null
      try {
        const [universityResponse, surveysResponse] = await Promise.all([
          axios.get(`/universities/${this.selectedUniversityId}`),
          axios.get(`/admin/surveys?universityId=${encodeURIComponent(this.selectedUniversityId)}`),
        ])
        this.universityDetail = universityResponse.data
        this.surveys = (surveysResponse.data && surveysResponse.data.surveys) || []
        this.previewCeremonyId = this.ceremonies.length ? this.ceremonies[0].id : ''
        if (this.surveys.length) await this.selectSurvey(this.surveys[0].id)
        else this.creating = true
      } catch (error) {
        this.pageError = 'Could not load surveys for this university.'
      } finally {
        this.loading = false
      }
    },
    startCreating() {
      this.creating = true
      this.copying = false
      this.newSurveyName = ''
    },
    startCopying() {
      this.copying = true
      this.creating = false
      this.copySource = ''
      this.copySurveyName = ''
      this.copyTargetCeremonyIds = []
    },
    async createSurvey() {
      if (!this.newSurveyName || this.saving) return
      this.saving = true
      this.pageError = ''
      try {
        const definition = {
          schemaVersion: 1,
          title: this.newSurveyName,
          description: '',
          fields: [],
        }
        const response = await axios.post('/admin/surveys', {
          universityId: this.selectedUniversityId,
          name: this.newSurveyName,
          definition,
          ceremonyIds: [],
        })
        this.creating = false
        this.newSurveyName = ''
        await this.refreshSurveyList(response.data.id)
        trackAdminEvent('survey_draft_created', { toolKey: 'survey_studio' })
      } catch (error) {
        this.pageError = this.apiError(error, 'Could not create the survey draft.')
      } finally {
        this.saving = false
      }
    },
    async refreshSurveyList(selectedId) {
      const response = await axios.get(`/admin/surveys?universityId=${encodeURIComponent(this.selectedUniversityId)}`)
      this.surveys = (response.data && response.data.surveys) || []
      if (selectedId) await this.selectSurvey(selectedId)
    },
    async copySurvey() {
      if (!this.canCopySurvey || this.saving) return
      this.saving = true
      this.pageError = ''
      try {
        const response = await axios.post('/admin/surveys/copy', {
          universityId: this.selectedUniversityId,
          ...(this.copySource.startsWith('version:') ? { sourceVersionId: this.copySource.slice(8) } : { sourceCeremonyId: this.copySource }),
          ceremonyIds: this.copyTargetCeremonyIds,
          name: this.copySurveyName,
        })
        this.copying = false
        await this.refreshSurveyList(response.data.id)
        trackAdminEvent('survey_draft_copied', {
          toolKey: 'survey_studio',
          metadata: { target_count: this.copyTargetCeremonyIds.length },
        })
      } catch (error) {
        this.pageError = this.apiError(error, 'Could not copy this ceremony survey.')
      } finally {
        this.saving = false
      }
    },
    async selectSurvey(surveyId) {
      this.loading = true
      this.pageError = ''
      try {
        const response = await axios.get(`/admin/surveys/${surveyId}`)
        this.hydrateSurvey(response.data)
      } catch (error) {
        this.pageError = 'Could not open this survey.'
      } finally {
        this.loading = false
      }
    },
    hydrateSurvey(survey, versionId) {
      this.hydrating = true
      this.survey = survey
      this.surveyName = survey.name
      const version = survey.versions.find(item => item.id === versionId) || survey.draftVersion || survey.publishedVersion
      this.selectedVersionId = version.id
      this.applyCeremonyIds = [...(version.ceremonyIds || [])]
      this.applicationReview = null
      this.lifecycleError = ''
      this.lifecycleMessage = ''
      this.definition = this.editableDefinition(version.definition, new Set((version.definition.fields || []).map(field => field.name)))
      this.targetCeremonyIds = [...(version.ceremonyIds || [])]
      if (version.status === 'PUBLISHED') {
        this.previewCeremonyId = (version.assignedCeremonyIds || [])[0] || (version.ceremonyIds || [])[0] || (this.ceremonies[0] || {}).id || ''
      }
      this.publishGate = survey.publishGate || emptyPublishGate()
      this.previewMessage = ''
      this.health = null
      this.selectedFieldIndex = this.definition.fields.length ? 0 : -1
      this.validationErrors = []
      this.validationMessage = ''
      this.dirty = false
      this.savedAt = ''
      this.$nextTick(() => {
        this.hydrating = false
        if (this.isPublished) this.loadHealth()
      })
    },
    async saveSurvey() {
      if (this.jsonEditing || !this.survey || !this.draftVersion.id) return
      this.validationErrors = this.questionKeyErrors()
      if (this.validationErrors.length) {
        this.validationMessage = 'Fix these items before saving'
        return
      }
      this.saving = true
      this.validationErrors = []
      this.validationMessage = ''
      try {
        const response = await axios.put(`/admin/surveys/${this.survey.id}`, {
          surveyVersionId: this.draftVersion.id,
          name: this.surveyName,
          definition: this.cleanDefinition(),
          ceremonyIds: this.targetCeremonyIds,
        })
        this.hydrateSurvey(response.data)
        this.savedAt = 'just now'
        trackAdminEvent('survey_draft_saved', {
          toolKey: 'survey_studio',
          metadata: { question_count: this.definition.fields.length },
        })
      } catch (error) {
        const data = error && error.response && error.response.data
        this.validationErrors = (data && data.errors) || []
        this.validationMessage = this.validationErrors.length ? 'Fix these items before saving' : this.apiError(error, 'Could not save this draft.')
      } finally {
        this.saving = false
      }
    },
    async checkSurvey() {
      if (this.jsonEditing) return
      this.validationErrors = this.questionKeyErrors()
      if (this.validationErrors.length) {
        this.validationMessage = 'Fix these items before checking'
        return
      }
      this.validationMessage = ''
      try {
        const response = await axios.post('/admin/surveys/validate', {
          definition: this.cleanDefinition(),
          forPublish: false,
        })
        this.validationErrors = response.data.errors || []
        this.validationMessage = this.validationErrors.length ? 'This survey needs attention' : 'Everything in this draft is structurally valid.'
      } catch (error) {
        const data = error && error.response && error.response.data
        this.validationErrors = (data && data.errors) || []
        this.validationMessage = this.validationErrors.length ? 'This survey needs attention' : 'Could not check this survey.'
      }
    },
    editableDefinition(value, originalKeys) {
      const definition = JSON.parse(JSON.stringify(value))
      definition.fields = (definition.fields || []).map(field => ({
        ...field,
        rules: field.rules || {},
        _editorId: this.newEditorId(),
        _originalKey: originalKeys.has(field.name) ? field.name : null,
        _suggestKey: false,
      }))
      return definition
    },
    openJsonEditor() {
      if (this.isPublished || this.saving || this.publishing || this.jsonEditing) return
      this.creating = false
      this.copying = false
      this.jsonText = JSON.stringify(this.cleanDefinition(), null, 2)
      this.jsonErrors = []
      this.jsonEditing = true
    },
    cancelJsonEditor() {
      if (this.jsonBusy) return
      this.jsonEditing = false
      this.jsonText = ''
      this.jsonErrors = []
    },
    async importJsonFile(event) {
      const file = event.target.files[0]
      if (!file || this.jsonBusy || !this.jsonEditing) return
      this.jsonBusy = true
      this.jsonErrors = []
      try {
        this.jsonText = await file.text()
      } catch (error) {
        this.jsonErrors = [{ message: 'Could not read this file. Try again or paste its JSON.' }]
      } finally {
        this.jsonBusy = false
        event.target.value = ''
      }
    },
    async applyJson() {
      if (!this.jsonEditing || this.jsonBusy || this.isPublished) return
      this.jsonErrors = []
      let value
      try {
        value = JSON.parse(this.jsonText)
      } catch (error) {
        this.jsonErrors = [{ message: 'Invalid JSON. Check quotes, commas and brackets.' }]
        return
      }
      this.jsonBusy = true
      try {
        const response = await axios.post('/admin/surveys/validate', { definition: value, forPublish: false })
        const data = response.data
        if (!data.valid || !data.definition) {
          this.jsonErrors = data.errors && data.errors.length ? data.errors : [{ message: 'Could not validate this definition.' }]
          return
        }
        // Only saved keys may retain legacy spelling. Imported editor metadata is untrusted.
        const originalKeys = new Set(this.definition.fields.filter(field => field.name === field._originalKey).map(field => field.name))
        const candidate = this.editableDefinition(data.definition, originalKeys)
        this.jsonErrors = this.questionKeyErrors(candidate.fields)
        if (this.jsonErrors.length) return
        this.definition = candidate
        this.selectedFieldIndex = candidate.fields.length ? 0 : -1
        this.dirty = true
        this.validationErrors = []
        this.validationMessage = 'JSON checked and added to the draft. Review the preview, then save.'
        this.jsonEditing = false
        this.jsonText = ''
      } catch (error) {
        const data = error && error.response && error.response.data
        this.jsonErrors = (data && data.errors && data.errors.length) ? data.errors : [{ message: 'Could not check this JSON. Try again.' }]
      } finally {
        this.jsonBusy = false
      }
    },
    cleanDefinition() {
      const definition = JSON.parse(JSON.stringify(this.definition))
      definition.fields.forEach(field => {
        delete field._editorId
        delete field._suggestKey
        delete field._originalKey
        delete field.primary
        delete field.secondary
      })
      return definition
    },
    selectField(index) {
      this.selectedFieldIndex = index
    },
    addQuestion() {
      const type = this.newQuestionType
      if (!type) return
      let sequence = this.definition.fields.length + 1
      while (this.definition.fields.some(field => field.name === `new_question_${sequence}`)) sequence++
      const field = {
        name: `new_question_${sequence}`,
        questionId: null,
        _editorId: this.newEditorId(),
        _suggestKey: true,
        title: type === 'presentationOnly' ? 'Information for participants' : 'New question',
        type,
        display: true,
        rules: { required: false },
        placeholder: '',
        titleFormat: 'text',
      }
      if (CHOICE_TYPES.includes(type)) {
        field.choices = [
          { text: 'Option 1', value: 'option_1' },
          { text: 'Option 2', value: 'option_2' },
        ]
        field.default = 'Choose an option'
      }
      this.definition.fields.push(field)
      this.selectedFieldIndex = this.definition.fields.length - 1
      this.newQuestionType = ''
    },
    updateQuestionTitle(title) {
      this.selectedField.title = title
      if (!this.isInformationField(this.selectedField) && this.selectedField._suggestKey && title.trim() && title !== 'New question') {
        const key = suggestQuestionKey(title)
        this.updateQuestionKey(key)
      }
    },
    updateQuestionKey(key) {
      if (!this.selectedField) return
      const oldKey = this.selectedField.name
      this.selectedField.name = key
      this.selectedField._suggestKey = false
      this.definition.fields.forEach(field => (field.conditions || []).forEach(condition => {
        if (condition.field === oldKey) condition.field = key
      }))
    },
    removeQuestion() {
      if (!this.selectedField) return
      const removedName = this.selectedField.name
      this.definition.fields.splice(this.selectedFieldIndex, 1)
      this.definition.fields.forEach(field => {
        if (!Array.isArray(field.conditions)) return
        field.conditions = field.conditions.filter(condition => condition.field !== removedName)
      })
      this.selectedFieldIndex = Math.min(this.selectedFieldIndex, this.definition.fields.length - 1)
    },
    moveQuestion(index, offset) {
      const target = index + offset
      if (target < 0 || target >= this.definition.fields.length) return
      const item = this.definition.fields.splice(index, 1)[0]
      this.definition.fields.splice(target, 0, item)
      this.selectedFieldIndex = target
    },
    changeQuestionType(type) {
      if (!this.selectedField) return
      const previousType = this.selectedField.type
      const textTypes = ['text', 'email']
      if (CHOICE_TYPES.includes(previousType) && !CHOICE_TYPES.includes(type)) {
        this.definition.fields.forEach(field => {
          const conditions = field.conditions || []
          const remaining = conditions.filter(condition => condition.field !== this.selectedField.name)
          if (remaining.length !== conditions.length) {
            this.$set(field, 'conditions', remaining)
            if (!remaining.length) this.$set(field, 'display', true)
          }
        })
      }
      if (!(textTypes.includes(previousType) && textTypes.includes(type))) {
        this.$set(this.selectedField, 'rules', { required: Boolean(this.selectedField.rules && this.selectedField.rules.required) })
        this.$delete(this.selectedField, 'regex')
      }
      if (previousType === 'markdown' && type !== 'markdown') this.$delete(this.selectedField, 'content')
      if (type === 'markdown' && previousType !== 'markdown') this.$set(this.selectedField, 'content', '')
      this.$set(this.selectedField, 'type', type)
      if (CHOICE_TYPES.includes(type) && !Array.isArray(this.selectedField.choices)) {
        this.$set(this.selectedField, 'choices', [
          { text: 'Option 1', value: 'option_1' },
          { text: 'Option 2', value: 'option_2' },
        ])
        this.$set(this.selectedField, 'default', 'Choose an option')
      }
      if (!CHOICE_TYPES.includes(type)) {
        if (this.selectedField.conditions) this.$set(this.selectedField, 'conditions', this.selectedField.conditions.filter(condition => !condition.operation || condition.operation.relates_to !== 'choices'))
        this.$delete(this.selectedField, 'choices')
        this.$delete(this.selectedField, 'default')
      }
      if (this.isInformationField(this.selectedField)) {
        this.$set(this.selectedField.rules, 'required', false)
        this.$delete(this.selectedField, 'primary')
        this.$delete(this.selectedField, 'secondary')
      }
    },
    addChoice() {
      let number = this.selectedField.choices.length + 1
      while (this.selectedField.choices.some(choice => choice.value === `option_${number}`)) number += 1
      this.selectedField.choices.push({ text: `Option ${number}`, value: `option_${number}` })
    },
    removeChoice(index) {
      this.selectedField.choices.splice(index, 1)
    },
    isChoiceField(field) {
      return Boolean(field && CHOICE_TYPES.includes(field.type))
    },
    conditionChoices(condition) {
      const source = this.definition.fields.find(field => field.name === condition.field)
      return (source && source.choices) || []
    },
    toggleCondition(enabled) {
      if (!this.selectedField) return
      const conditions = Array.isArray(this.selectedField.conditions) ? this.selectedField.conditions : []
      if (!enabled) {
        this.$set(this.selectedField, 'conditions', conditions.filter(condition => !this.displayConditions.includes(condition)))
        this.$set(this.selectedField, 'display', true)
        return
      }
      this.addCondition()
    },
    addCondition() {
      const source = this.conditionSources[0]
      if (!source) return
      this.$set(this.selectedField, 'display', false)
      const conditions = Array.isArray(this.selectedField.conditions) ? this.selectedField.conditions : []
      this.$set(this.selectedField, 'conditions', [...conditions, {
        field: source.name,
        value: source.choices[0] ? source.choices[0].value : '',
        comparator: 'equals',
        operation: { relates_to: 'display', type: 'enabled', enabled: true },
      }])
    },
    removeCondition(condition) {
      this.$set(this.selectedField, 'conditions', this.selectedField.conditions.filter(item => item !== condition))
      if (!this.displayConditions.length) this.$set(this.selectedField, 'display', true)
    },
    updateConditionSource(condition, name) {
      condition.field = name
      const source = this.definition.fields.find(field => field.name === name)
      condition.value = source && source.choices[0] ? source.choices[0].value : ''
    },
    typeLabel(type) {
      const found = this.questionTypes.find(item => item.value === type)
      return found ? found.label : type
    },
    ceremonyLabel(ceremony) {
      if (!ceremony) return ''
      const date = moment(ceremony.datetime)
      const dateLabel = date.isValid() ? date.format('ddd D MMM YYYY, HH:mm') : ceremony.datetime
      return ceremony.label ? `${ceremony.label} · ${dateLabel}` : dateLabel
    },
    handlePreviewSubmit() {
      this.previewMessage = 'Preview complete. No answers were saved.'
      trackAdminEvent('survey_preview_completed', { toolKey: 'survey_studio' })
    },
    async publishSurvey() {
      if (this.jsonEditing) return
      if (this.dirty || !this.publishGate.ready || this.publishing) return
      this.publishing = true
      this.pageError = ''
      try {
        const response = await axios.post(`/admin/surveys/${this.survey.id}/publish`, {
          surveyVersionId: this.draftVersion.id,
          definitionHash: this.draftVersion.definitionHash,
        })
        this.hydrateSurvey(response.data)
        this.surveys = this.surveys.map(item => item.id === response.data.id ? response.data : item)
        trackAdminEvent('survey_published', {
          toolKey: 'survey_studio',
          metadata: { version: this.workingVersion.version },
        })
      } catch (error) {
        this.pageError = this.apiError(error, 'Could not publish this survey.')
      } finally {
        this.publishing = false
      }
    },
    async amendSurvey() {
      if (this.lifecycleBusy || this.survey.draftVersion) return
      this.lifecycleBusy = true
      this.lifecycleError = ''
      try {
        const response = await axios.post(`/admin/surveys/${this.survey.id}/amend`, { surveyVersionId: this.workingVersion.id })
        this.hydrateSurvey(response.data)
        this.surveys = this.surveys.map(item => item.id === response.data.id ? response.data : item)
      } catch (error) {
        this.lifecycleError = this.apiError(error, 'Could not create the amendment.')
      } finally { this.lifecycleBusy = false }
    },
    async reviewApplication() {
      if (this.lifecycleBusy) return
      const key = this.applicationKey
      this.lifecycleBusy = true
      this.lifecycleError = ''
      this.lifecycleMessage = ''
      this.applicationReview = null
      try {
        const response = await axios.post(`/admin/surveys/${this.survey.id}/application-preview`, {
          surveyVersionId: this.workingVersion.id, ceremonyIds: this.applyCeremonyIds,
        })
        if (key === this.applicationKey) this.applicationReview = response.data
      } catch (error) {
        this.lifecycleError = this.apiError(error, 'Could not review these ceremonies.')
      } finally { this.lifecycleBusy = false }
    },
    async applyVersion() {
      if (!this.applicationReview || this.lifecycleBusy) return
      this.lifecycleBusy = true
      this.lifecycleError = ''
      const versionId = this.workingVersion.id
      try {
        const response = await axios.post(`/admin/surveys/${this.survey.id}/apply`, {
          surveyVersionId: versionId, ceremonyIds: this.applyCeremonyIds,
          expectedAssignments: this.applicationReview.expectedAssignments,
        })
        this.hydrateSurvey(response.data, versionId)
        this.surveys = this.surveys.map(item => item.id === response.data.id ? response.data : item)
        this.lifecycleMessage = 'Version applied to the selected ceremonies.'
      } catch (error) {
        this.applicationReview = null
        this.lifecycleError = this.apiError(error, 'Could not apply this version. Review the changes again before retrying.')
      } finally { this.lifecycleBusy = false }
    },
    assignedCount(survey) {
      return (survey.versions || []).reduce((count, version) => count + (version.assignedCount || 0), 0)
    },
    async loadHealth() {
      if (!this.survey || !this.isPublished) return
      const key = `${this.selectedSurveyId}:${this.selectedVersionId}`
      this.healthLoading = true
      try {
        const response = await axios.get(`/admin/surveys/${this.survey.id}/health?surveyVersionId=${encodeURIComponent(this.workingVersion.id)}`)
        if (key === `${this.selectedSurveyId}:${this.selectedVersionId}`) this.health = response.data
      } catch (error) {
        if (key === `${this.selectedSurveyId}:${this.selectedVersionId}`) this.lifecycleError = this.apiError(error, 'Could not load answer health.')
      } finally {
        if (key === `${this.selectedSurveyId}:${this.selectedVersionId}`) this.healthLoading = false
      }
    },
    ceremonyName(ceremonyId) {
      const ceremony = this.ceremonies.find(item => item.id === ceremonyId)
      return ceremony ? this.ceremonyLabel(ceremony) : ceremonyId
    },
    libraryVersion(survey) {
      return survey.draftVersion || survey.publishedVersion || { definition: { fields: [] }, targetCount: 0 }
    },
    formatTimestamp(value) {
      const date = moment(value)
      return date.isValid() ? date.fromNow() : value
    },
    apiError(error, fallback) {
      const data = error && error.response && error.response.data
      return (data && data.error) || fallback
    },
  },
}
</script>
